import type { Metric } from '@/schemas/screener'

/**
 * The table's column sets (the mock's Overview, Performance, Technicals… tabs) are mm-be's metric
 * groups, in the order `/metrics/` first lists them: a group mm-be adds is a tab here.
 */

export interface ColumnSet {
  /** The group key, which `?set=` names. */
  readonly id: string
  /** Sentence case, the filter dialog's group head: "Enterprise value". */
  readonly label: string
  /** Title Case, the tab's (owner, 2026-10-27: every inner tab is Title Case): "Enterprise Value". */
  readonly tabLabel: string
  readonly metrics: readonly Metric[]
  /** Overview's trailing Sector column, as the mock draws it. */
  readonly withSector: boolean
}

const OVERVIEW = 'overview'

/** The price every set but Overview leads with (the mock's `Td`), so a row can be read alone. */
const PRICE = 'ltp'

const capitalise = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1)

const groupWords = (group: string): string[] => group.split('_').filter((word) => word !== '')

/** `enterprise_value` → "Enterprise value". */
export const groupLabel = (group: string): string => capitalise(groupWords(group).join(' '))

const groupTabLabel = (group: string): string => groupWords(group).map(capitalise).join(' ')

export const columnSets = (metrics: readonly Metric[]): ColumnSet[] => {
  const groups = [...new Set(metrics.map((metric) => metric.group))]
  const price = metrics.find((metric) => metric.key === PRICE)
  return groups.map((group) => {
    const own = metrics.filter((metric) => metric.group === group)
    const lead = group !== OVERVIEW && price !== undefined && price.group !== group ? [price] : []
    return {
      id: group,
      label: groupLabel(group),
      tabLabel: groupTabLabel(group),
      metrics: [...lead, ...own],
      withSector: group === OVERVIEW,
    }
  })
}

/** The set `?set=` names, or the first when it names none this list has. */
export const pickSet = (
  sets: readonly ColumnSet[],
  id: string | undefined,
): ColumnSet | undefined => sets.find((set) => set.id === id) ?? sets[0]
